import { UpdateDeviceFlowLimitsInput } from '@/__generated__/graphql';
import { setSnackbar } from '@/slices/components';
import { useMutation, useSuspenseQuery } from '@apollo/client';
import NumericTextField from '@components/NumericTextField';
import { DeviceTabProps } from '@features/deviceDataConfigurationModal/components/tabs/types';
import UPDATE_DEVICE_FLOW_LIMITS from '@features/deviceDataConfigurationModal/graphql/mutations/updateDeviceFlowLimits';
import GET_DEVICE_FLOW_LIMITS from '@features/deviceDataConfigurationModal/graphql/queries/getRadarCurves';
import { flowLimitsSchema } from '@features/deviceDataConfigurationModal/utils/flowLimitsSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Typography,
} from '@mui/material';
import { useEffect, type FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

type FormValues = UpdateDeviceFlowLimitsInput;

const FlowLimitsTab: FC<DeviceTabProps> = ({ deviceId, onClose, setIsDirty }) => {
  const { _ } = useLingui();
  const { data } = useSuspenseQuery(GET_DEVICE_FLOW_LIMITS, { variables: { deviceId } });

  const dispatch = useDispatch();

  const defaultValues: FormValues = {
    deviceId,
    state: {
      isDry: data?.deviceDataConfiguration.state?.isDry ?? false,
    },
    flowLimits: {
      min: data?.deviceDataConfiguration.flowLimits?.min ?? null,
      max: data?.deviceDataConfiguration.flowLimits?.max ?? null,
    },
  };

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: zodResolver(flowLimitsSchema),
  });

  const [update, { loading: updateLoading }] = useMutation(UPDATE_DEVICE_FLOW_LIMITS, {
    onCompleted: ({ updateDeviceFlowLimits }) => {
      methods.reset({
        deviceId: updateDeviceFlowLimits.deviceId,
        state: {
          isDry: updateDeviceFlowLimits.state?.isDry ?? false,
        },
        flowLimits: {
          min: updateDeviceFlowLimits.flowLimits?.min ?? null,
          max: updateDeviceFlowLimits.flowLimits?.max ?? null,
        },
      });
      dispatch(
        setSnackbar({
          open: true,
          message: _(msg`Dato actualizado correctamente`),
          severity: 'success',
        }),
      );
    },
    onError: (errors) => {
      dispatch(
        setSnackbar({
          open: true,
          message: `${_(msg`Error al actualizar el dato`)}: ${errors.message}`,
          severity: 'error',
        }),
      );
    },
  });

  const { isDirty, isSubmitting } = methods.formState;

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty, setIsDirty]);

  const submitHandler = async (formData: FormValues) => {
    update({ variables: { input: formData } });
  };

  return (
    <form onSubmit={methods.handleSubmit(submitHandler)}>
      <DialogContent
        sx={{
          maxWidth: 'md',
          minHeight: 500,
          margin: 'auto',
          borderBottom: '1px solid',
          borderColor: 'divider',
          pb: 0,
        }}
      >
        <Typography variant="h5" mb={2}>
          <Trans>Límites de Caudal</Trans>
        </Typography>
        <Box display="grid" gridTemplateColumns="repeat(3, 1fr)" fontSize="small" gap={2} mb={3}>
          <Controller
            name="flowLimits.min"
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <NumericTextField
                {...field}
                size="small"
                label={_(msg`Caudal mínimo`)}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <Controller
            name="flowLimits.max"
            control={methods.control}
            render={({ field, fieldState: { error } }) => (
              <NumericTextField
                {...field}
                size="small"
                label={_(msg`Caudal máximo`)}
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Box>
        <Box>
          <Controller
            name="state.isDry"
            control={methods.control}
            render={({ field: { value, ref, ...rest } }) => (
              <FormGroup>
                <FormControlLabel
                  {...rest}
                  checked={value}
                  inputRef={ref}
                  control={<Checkbox />}
                  label={_(msg`Activar canal seco`)}
                />
                <FormHelperText sx={{ color: '#FF9E42' }}>
                  <Trans>
                    <b>¡PRECAUCIÓN!</b> Activar esto causará que los datos del dispositivo se
                    guarden como 0.
                  </Trans>
                </FormHelperText>
              </FormGroup>
            )}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ maxWidth: 'md', margin: 'auto', py: 2 }}>
        <Button
          disabled={!isDirty}
          color="info"
          variant="outlined"
          onClick={() => methods.reset(defaultValues)}
        >
          <Trans>Restablecer Formulario</Trans>
        </Button>
        <Box flexGrow={1} />
        <Button color="info" variant="outlined" onClick={() => onClose()}>
          <Trans>Cerrar</Trans>
        </Button>
        <Button
          disabled={!isDirty}
          loading={isSubmitting || updateLoading}
          type="submit"
          variant="contained"
        >
          <Trans>Guardar</Trans>
        </Button>
      </DialogActions>
    </form>
  );
};

export default FlowLimitsTab;
